import { useForm, Controller } from "react-hook-form";
import {
  formBtn1,
  inputClass,
  pageClass,
  pageHeader,
} from "../Utils/CustomClasses";
import Select from "react-select";
import Error from "../Error/Error";
import { useEffect, useState } from "react";
import {
  GetAllCompanyAPI,
  StaticUserAPI,
  teamLeadMappingAPI,
} from "../../API/Api";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

export default function TeamChanges() {
  const {
    register,
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const companyId = watch("companyId");
  const [assignUsers, setAssignUsers] = useState([]);
  const [company, setCompany] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");

  const userDetails = useSelector((state) => state?.login?.userDetails);
  const isAdmin = userDetails?.role?.roleName === "Admin";
  const isSuperAdmin = userDetails?.role?.roleName === "SuperAdmin";

  const assignToOptions = assignUsers?.map((user) => ({
    value: user?._id,
    label: `${user?.first_name} ${user?.last_name}`,
  }));

  // ---------------- APIs ----------------

  const getAllCompany = async (companyId) => {
    try {
      if (isSuperAdmin) {
        const res = await GetAllCompanyAPI(companyId);
        setCompany(res?.data?.filteredData || []);
      }
    } catch (err) {
      console.log("Error:", err);
    }
  };

  const handleCompanySelect = async (id) => {
    setSelectedCompanyId(id);
    try {
      const response = await StaticUserAPI({
        id: userDetails?._id,
        role: userDetails?.role?.roleName,
        company: id,
      });
      setAssignUsers(response?.data?.filteredData || []);
    } catch (err) {
      console.log("Error", err);
    }
  };

  const handleAssignSearch = async (searchValue) => {
    try {
      const response = await StaticUserAPI({
        id: userDetails?._id,
        role: userDetails?.role?.roleName,
        search: searchValue,
        company: isAdmin
          ? userDetails?.company?._id
          : selectedCompanyId,
      });
      setAssignUsers(response?.data?.filteredData || []);
    } catch (error) {
      console.error("Error assigning user:", error);
    }
  };

  const onSubmit = async (data) => {
    const payload = {
      companyId: isAdmin
        ? userDetails?.company?._id
        : selectedCompanyId,
      user_from: data.change_from?.value,
      user_to: data.change_to?.value,
    };

    try {
      const response = await teamLeadMappingAPI(payload);
      if (response?.status === 200) {
        toast.success(response?.data?.message);
        reset({
          companyId: "",
          change_from: null,
          change_to: null,
        });
        setAssignUsers([]);
      } else {
        toast.error(response?.error);
      }
    } catch (err) {
      console.log("err", err);
    }
  };

  // ---------------- Effects ----------------

  useEffect(() => {
    getAllCompany(userDetails?.company?._id);
  }, []);

  useEffect(() => {
    if (isAdmin) {
      handleCompanySelect(userDetails?.company?._id);
    }
  }, []);

  // ---------------- Select Styles ----------------

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      width: "100%", // removed fixed width
      padding: "5px 0px",
      border: "2px solid rgba(228, 228, 231, 0.7)",
      borderRadius: "6px",
      boxShadow: state.isFocused ? "none" : provided.boxShadow,
      borderColor: state.isFocused
        ? "#007BFF"
        : "rgba(228, 228, 231, 0.7)",
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: "220px",
    }),
  };

  // ---------------- JSX ----------------

  return (
    <div className={pageClass}>
      <div className="bg-white p-20 rounded-xl">
        <div className="flex items-center justify-between">
          <h1 className={pageHeader}>Team Changes</h1>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          {/* Main Row */}
          <div
            className={`flex ${
              isAdmin
                ? "items-end gap-10"
                : "items-start justify-between"
            }`}
          >
            {/* Company (SuperAdmin only) */}
            {isSuperAdmin && (
              <div className="w-72">
                <p className="text-black mb-2">Company</p>
                <select
                  className={`${inputClass} !bg-slate-100 ${
                    companyId === "" ? "text-slate-400" : ""
                  }`}
                  {...register("companyId")}
                  onChange={(e) =>
                    handleCompanySelect(e.target.value)
                  }
                >
                  <option value="">Company Name</option>
                  {company?.map((c) => (
                    <option key={c?._id} value={c?._id}>
                      {c?.companyName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Change From */}
            <div className="w-72">
              <p className="text-black mb-2">Change From</p>
              <Controller
                name="change_from"
                control={control}
                rules={{ required: "Required*" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value || null}
                    options={assignToOptions}
                    placeholder="Select User"
                    styles={customStyles}
                    isClearable={true}
                    onChange={(option) => {
                      field.onChange(option);
                      handleAssignSearch(option?.label);
                    }}
                    onInputChange={(input, { action }) => {
                      if (action === "input-change") {
                        handleAssignSearch(input);
                      }
                    }}
                  />
                )}
              />
              {errors.change_from && (
                <Error title={errors.change_from.message} />
              )}
            </div>

            {/* Change To */}
            <div className="w-72">
              <p className="text-black mb-2">Change To</p>
              <Controller
                name="change_to"
                control={control}
                rules={{ required: "Required*" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value || null}
                    options={assignToOptions}
                    placeholder="Select User"
                    styles={customStyles}
                    isClearable={true}
                    onChange={(option) => {
                      field.onChange(option);
                      handleAssignSearch(option?.label);
                    }}
                    onInputChange={(input, { action }) => {
                      if (action === "input-change") {
                        handleAssignSearch(input);
                      }
                    }}
                  />
                )}
              />
              {errors.change_to && (
                <Error title={errors.change_to.message} />
              )}
            </div>

            {/* Submit Admin */}
            {isAdmin && (
              <div>
                <button
                  type="submit"
                  className={`${formBtn1} px-8`}
                >
                  Submit
                </button>
              </div>
            )}
          </div>

          {/* SuperAdmin Submit Button */}
          {isSuperAdmin && (
            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                className={`${formBtn1} w-full`}
              >
                Submit
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}